import React, { useState, useEffect } from 'react';
import { DollarSign, Truck, Check, Settings } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент сайдбара для быстрой оценки стоимости бетона
 */
const CostEstimationSidebar = ({ 
  formData, 
  setFormData, 
  results, 
  regions, 
  concretePriceBase,
  errors,
  isCalculating
}) => {
  const [estimatedVolume, setEstimatedVolume] = useState(1);
  const [estimatedCost, setEstimatedCost] = useState(0);

  // Обновляем стоимость при изменении ключевых параметров
  useEffect(() => {
    // Получаем базовую цену в зависимости от марки бетона
    const basePrice = concretePriceBase[formData.concreteGrade] || 4000;
    
    // Применяем региональный коэффициент
    const selectedRegion = regions.find(r => r.id === formData.regionPrice) || regions[0];
    const regionMultiplier = selectedRegion.priceModifier;
    
    // Рассчитываем базовую стоимость
    const basicCost = basePrice * estimatedVolume * regionMultiplier;
    
    // Если включена доставка, добавляем ее стоимость
    let totalCost = basicCost;
    
    if (formData.includeDelivery) {
      // Примерная стоимость доставки: 150 руб/км за машину 7м³
      const deliveryTruckCapacity = 7; // м³
      const deliveryPricePerKm = 150;
      const truckCount = Math.ceil(estimatedVolume / deliveryTruckCapacity);
      
      const deliveryCost = parseFloat(formData.deliveryDistance) * deliveryPricePerKm * truckCount;
      totalCost += deliveryCost;
    }
    
    // Стоимость насоса (если выбрано)
    if (formData.includePump) {
      const pumpHourlyRate = 2000;
      const pumpMinHours = 4;
      // Примерно можно подать 15м³/час
      const pumpHours = Math.max(pumpMinHours, Math.ceil(estimatedVolume / 15));
      totalCost += pumpHourlyRate * pumpHours;
    }
    
    // Стоимость работ (если выбрано)
    if (formData.includeLabor) {
      totalCost += parseFloat(formData.laborCost) * estimatedVolume;
    }
    
    // Обновляем расчетную стоимость
    setEstimatedCost(totalCost);
  }, [formData, estimatedVolume, regions, concretePriceBase]);

  // Обработчик изменения объема
  const handleVolumeChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setEstimatedVolume(Math.max(0.1, value));
  };

  // Обработчик изменения марки бетона
  const handleGradeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      concreteGrade: e.target.value
    }));
  };

  // Обработчик изменения региона
  const handleRegionChange = (e) => {
    setFormData(prev => ({
      ...prev,
      regionPrice: e.target.value
    }));
  };

  // Включение/выключение опций
  const toggleOption = (option) => {
    setFormData(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Выбираем марку бетона в зависимости от типа проекта
  const suggestConcreteGrade = (projectType) => {
    switch (projectType) {
      case 'foundation':
        return 'M200';
      case 'floor':
        return 'M250';
      case 'wall':
        return 'M300';
      case 'column':
        return 'M350';
      case 'pool':
        return 'M300';
      default:
        return 'M200';
    }
  };

  // Рекомендуемая марка бетона для выбранного типа проекта
  const recommendedGrade = suggestConcreteGrade(formData.projectType);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <DollarSign className="w-5 h-5 mr-2 text-emerald-500" />
        Оценка стоимости
      </h3>
      
      <div className="space-y-4">
        {/* Объем бетона */}
        <div>
          <label htmlFor="estimatedVolume" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Объем бетона (м³)
          </label>
          <input
            type="number"
            id="estimatedVolume"
            value={estimatedVolume}
            onChange={handleVolumeChange}
            className="form-input w-full"
            step="0.1"
            min="0.1"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Укажите примерный объем для оценки стоимости
          </p>
        </div>

        {/* Марка бетона */}
        <div>
          <label htmlFor="quickConcreteGrade" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Марка бетона
          </label>
          <select
            id="quickConcreteGrade"
            value={formData.concreteGrade}
            onChange={handleGradeChange}
            className="form-select w-full"
          >
            {Object.keys(concretePriceBase).map((grade) => (
              <option key={grade} value={grade}>
                {grade} - {formatCurrency(concretePriceBase[grade], 0)}/м³
              </option>
            ))}
          </select>
          {recommendedGrade !== formData.concreteGrade && (
            <p className="mt-1 text-xs text-amber-500 dark:text-amber-400">
              Для {formData.projectType === 'foundation' ? 'фундамента' : 
                     formData.projectType === 'floor' ? 'пола' : 
                     formData.projectType === 'wall' ? 'стен' : 
                     formData.projectType === 'column' ? 'колонн' : 
                     'этого типа конструкции'} 
              рекомендуется {recommendedGrade}
            </p>
          )}
        </div>

        {/* Регион */}
        <div>
          <label htmlFor="quickRegion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Регион
          </label>
          <select
            id="quickRegion"
            value={formData.regionPrice}
            onChange={handleRegionChange}
            className="form-select w-full"
          >
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name} ({Math.round(region.priceModifier * 100)}% от базовой)
              </option>
            ))}
          </select>
        </div>

        {/* Дополнительные услуги */}
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="form-checkbox mr-2"
                checked={formData.includeDelivery}
                onChange={() => toggleOption('includeDelivery')}
              />
              Доставка
            </label>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {`${formData.deliveryDistance} км`}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="form-checkbox mr-2"
                checked={formData.includePump}
                onChange={() => toggleOption('includePump')}
              />
              Бетононасос
            </label>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ~2000 ₽/час
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="form-checkbox mr-2"
                checked={formData.includeLabor}
                onChange={() => toggleOption('includeLabor')}
              />
              Работы по заливке
            </label>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {`${formData.laborCost} ₽/м³`}
            </span>
          </div>
        </div>

        {/* Результаты оценки */}
        <div className="mt-4 bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Стоимость бетона:</span>
            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
              {formatCurrency(concretePriceBase[formData.concreteGrade] * estimatedVolume * (regions.find(r => r.id === formData.regionPrice)?.priceModifier || 1), 0)}
            </span>
          </div>
          
          {formData.includeDelivery && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Доставка:</span>
              <span className="text-sm text-emerald-700 dark:text-emerald-300">
                {formatCurrency(Math.ceil(estimatedVolume / 7) * parseFloat(formData.deliveryDistance) * 150, 0)}
              </span>
            </div>
          )}
          
          {formData.includePump && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Бетононасос:</span>
              <span className="text-sm text-emerald-700 dark:text-emerald-300">
                {formatCurrency(Math.max(4, Math.ceil(estimatedVolume / 15)) * 2000, 0)}
              </span>
            </div>
          )}
          
          {formData.includeLabor && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Работы:</span>
              <span className="text-sm text-emerald-700 dark:text-emerald-300">
                {formatCurrency(parseFloat(formData.laborCost) * estimatedVolume, 0)}
              </span>
            </div>
          )}
          
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-emerald-200 dark:border-emerald-700">
            <span className="text-base font-bold text-emerald-800 dark:text-emerald-300">ИТОГО:</span>
            <span className="text-base font-bold text-emerald-700 dark:text-emerald-300">
              {formatCurrency(estimatedCost, 0)}
            </span>
          </div>
          
          <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 text-center">
            {formatCurrency(estimatedCost / estimatedVolume, 0)} за 1 м³
          </div>
        </div>

        {/* Совет по минимизации стоимости */}
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
          <div className="flex items-start mb-1">
            <Check className="w-4 h-4 text-emerald-500 mr-1 flex-shrink-0 mt-0.5" />
            <span>Для точного расчета заполните все параметры и нажмите кнопку "Рассчитать объем бетона"</span>
          </div>
          <div className="flex items-start">
            <Settings className="w-4 h-4 text-emerald-500 mr-1 flex-shrink-0 mt-0.5" />
            <span>Стоимость может варьироваться в зависимости от поставщика и сезона.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostEstimationSidebar;